import { gql, useQuery } from '@apollo/client'
import * as d3 from 'd3-hierarchy'
import { useEffect, useMemo, useState } from 'react'

export interface IDataType {
  items?: {
    value: string[][]
  }
  burgerworksItems: {
    value: string[][]
    onChange: (value: string[][]) => void
    options: {
      id: string
      parentId: string
      displayName: string
    }[]
  }
  burgerworksCategories: {
    value: string[][]
    onChange: (value: string[][]) => void
    options: {
      id: string
      parentId: string
      displayName: string
    }[]
  }
}

interface IQueryDataType {
  listItems: {
    nodes: {
      itemId: number
      itemType: string
      itemDisplayName: string
      displayParentCategoryId: number
      displayParentCategoryName: string
    }[]
  }
  nestedData: {
    nodes: {
      dateTime: string
      data: {
        id: string
        parentId: string
        displayName: string
      }[]
    }[]
  }
}

const query = gql`
  query salesMixItems {
    listItems(iRoot: "EXPO_MENU_CATEGORY_ROOT") {
      nodes {
        itemId
        itemType
        itemDisplayName
        displayParentCategoryId
        displayParentCategoryName
      }
    }

    nestedData: customizedData(
      first: 1
      condition: { customizationType: "PMIX_CATEGORIES" }
      orderBy: [DATETIME_DESC]
    ) {
      nodes {
        datetime
        data
      }
    }
  }
`

const useBurgerworksSalesMixItems = () => {
  const { data } = useQuery<IQueryDataType>(query)
  const burgerworksItemsHooks = useState<string[][]>([[]])
  const burgerworksCategoriesHooks = useState<string[][]>([[]])
  const root = useMemo(() => {
    const rootData = data?.nestedData.nodes[0]?.data
    const rootId = rootData?.find((i) => i.parentId === null)?.id

    return rootData?.find((r) => r.id === rootId?.replace(/-all/, ''))
  }, [data])

  const burgerworksItemsOptions = useMemo(() => {
    const items = data?.listItems.nodes

    return !items || !root
      ? []
      : [
          {
            id: root.id,
            parentId: 'root',
            displayName: root.displayName,
          },
          ...items
            .filter(
              (n) =>
                n.displayParentCategoryId.toString() ===
                burgerworksCategoriesHooks[0][0].slice(-1)[0],
            )
            .map((n) => ({
              id: n.itemId.toString(),
              parentId: 'root',
              displayName: n.itemDisplayName,
            })),
        ]
  }, [data, root, burgerworksCategoriesHooks])

  useEffect(() => {
    if (root && burgerworksCategoriesHooks[0][0].length === 0)
      burgerworksCategoriesHooks[1]([[root.id]])
  }, [root, burgerworksCategoriesHooks])

  useEffect(() => {
    if (root) {
      if (
        burgerworksItemsHooks[0][0].length === 0 ||
        !burgerworksItemsOptions.some(
          (b) => b.id === burgerworksItemsHooks[0][0].slice(-1)[0],
        )
      )
        burgerworksItemsHooks[1]([[root.id]])
    }
  }, [root, burgerworksItemsHooks, burgerworksItemsOptions])

  return {
    items: useMemo((): IDataType['items'] => {
      const itemId = burgerworksItemsHooks[0][0].slice(-1)[0]

      return {
        value: [
          [
            burgerworksCategoriesHooks[0][0].slice(-1)[0],
            ...(itemId === root?.id ? [] : [itemId]),
          ],
        ],
      }
    }, [root, burgerworksCategoriesHooks, burgerworksItemsHooks]),

    burgerworksItems: useMemo(
      (): IDataType['burgerworksItems'] => ({
        value: burgerworksItemsHooks[0],
        onChange: burgerworksItemsHooks[1],
        options: burgerworksItemsOptions,
      }),
      [burgerworksItemsHooks, burgerworksItemsOptions],
    ),

    burgerworksCategories: useMemo((): IDataType['burgerworksCategories'] => {
      const items = data?.nestedData.nodes[0]?.data || []

      return {
        value: burgerworksCategoriesHooks[0],
        onChange: burgerworksCategoriesHooks[1],
        options: items
          .filter((d) => d.parentId !== null)
          .map((d) => ({
            ...d,
            id: d.id,
            parentId:
              d.parentId === `${root?.id}-all` ? 'root' : d.parentId.toString(),
          })),
      }
    }, [data, root, burgerworksCategoriesHooks]),
  }
}

export default useBurgerworksSalesMixItems
